import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import fleetMark from "../../assets/brand/command-center-mark-blue.svg";
import ProfileButton from "../ProfileButton/ProfileButton";
import { ReactComponent as AddIcon } from "../../assets/icons/add-icon.svg";
import { ReactComponent as SearchIcon } from "../../assets/icons/search-icon.svg";

import { debounce } from "lodash";
import "./Header.scss";
import SearchService from "../../services/SearchService";
import { ReactComponent as JobIcon } from "../../assets/icons/jobs-icon.svg";
import { ReactComponent as ContainerIcon } from "../../assets/icons/container-icon.svg";
import JobStatus from "../Jobs/JobStatus";
import ContainerType from "../ContainerType/ContainerType";
import PermissionGuard from "../PermissionGuard/PermissionGuard";
import { PERMISSIONS } from "../../hooks/usePermissions";

interface SearchItem {
  id: string;
  title: string;
  subtitle?: string;
  type?: "job" | "container" | "customer";
  size?: number;
  containerType?: string;
  jobCode?: string;
  code?: string;
}

interface HeaderProps { onToggleNavigation?: () => void; navigationOpen?: boolean; }

const Header: React.FC<HeaderProps> = ({ onToggleNavigation, navigationOpen }) => {
  const location = useLocation();
  const isLiveFleet = location.pathname.startsWith("/live-fleet");
  const currentPage = location.pathname.substring(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputFocus, setInputFocus] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const showJobsSearch = [
    "/jobs/all",
    "/jobs/initiated",
    "/jobs/assigned",
    "/jobs/in-progress",
    "/jobs/completed",
    "/jobs/cancelled",
    "/jobs/deleted",
  ].includes(currentPage);

  // Debounced search function
  const debouncedSearch = React.useCallback(
    debounce(async (query: string) => {
      if (!query) {
        setSearchResults([]);
        return;
      }

      try {
        setIsSearching(true);
        const response = await SearchService.searchJobsOrContainers({
          limit: 10,
          skip: 0,
          search: query,
        });

        const searchData = response.data.data;
        const formattedResults = (searchData.content || [])
          .map((item: any) => {
            console.log("Processing item:", {
              jobCode: item.jobCode,
              jobBreakups: item.jobBreakups,
              query: query,
            });

            const isJobMatch = item.jobCode
              ?.toLowerCase()
              .includes(query.toLowerCase());

            // Check container numbers in jobBreakups
            const isContainerMatch = item.jobBreakups?.some((breakup: any) =>
              breakup.jobBreakUpDetails?.some((detail: any) =>
                detail.containerNumber
                  ?.toString()
                  .toLowerCase()
                  .includes(query.toLowerCase())
              )
            );

            console.log("Match results:", {
              isJobMatch,
              isContainerMatch,
            });

            // If it's a container match, get the matching container number and details
            const matchingContainer = isContainerMatch
              ? item.jobBreakups
                  ?.find((breakup: any) =>
                    breakup.jobBreakUpDetails?.some((detail: any) =>
                      detail.containerNumber
                        ?.toString()
                        .toLowerCase()
                        .includes(query.toLowerCase())
                    )
                  )
                  ?.jobBreakUpDetails?.find((detail: any) =>
                    detail.containerNumber
                      ?.toString()
                      .toLowerCase()
                      .includes(query.toLowerCase())
                  )
              : null;

            return {
              id: item.jobId?.toString() || matchingContainer?.containerNumber,
              title: isContainerMatch
                ? matchingContainer?.containerNumber
                : decodeURIComponent(item.jobCode),
              subtitle: item.jobStatus || item.tripStatus,
              type: isJobMatch ? "job" : isContainerMatch ? "container" : null,
              size: matchingContainer?.containerSize,
              containerType: matchingContainer?.containerType,
              jobCode: decodeURIComponent(item.jobCode),
              code: decodeURIComponent(item.jobCode),
            };
          })
          .filter((item: SearchItem) => item.type !== null);

        console.log("Formatted results:", formattedResults);
        setSearchResults(formattedResults);
        setShowDropdown(true);
      } catch (error) {
        console.error("Error fetching search data:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.length > 0) {
      debouncedSearch(query);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  };

  // Handle item selection
  const handleItemSelect = (item: SearchItem) => {
    const type = item.type || "job";
    setShowDropdown(false);

    // Convert job status to URL-friendly format
    const getJobStatusPath = (status: string | undefined): string => {
      if (!status) return "all";
      switch (status.toUpperCase()) {
        case "INITIATED":
          return "initiated";
        case "ASSIGNED":
          return "assigned";
        case "STARTED":
        case "IN_PROGRESS":
          return "in-progress";
        case "COMPLETED":
          return "completed";
        case "CANCELLED":
          return "cancelled";
        case "DELETED":
          return "deleted";
        default:
          return "all";
      }
    };

    if (type === "job") {
      console.log("Job ID:", item.id);
      const jobStatus = getJobStatusPath(item.subtitle);
      navigate(
        `/jobs/${jobStatus}/${item.id}/${encodeURIComponent(item.code || "")}`
      );
    } else if (type === "container") {
      console.log("Container ID:", item.id);
      // For containers, we need to get the job status from the parent job
      const jobStatus = getJobStatusPath(item.subtitle);

      // Always navigate to containers view with the container number as search parameter
      navigate(
        `/jobs/${jobStatus}/${item.id}/${encodeURIComponent(
          item.code || ""
        )}/containers?container=${encodeURIComponent(item.title)}`
      );
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Split search results into jobs and containers
  const jobs = searchResults.filter((item) => item.type === "job");
  const containers = searchResults.filter((item) => item.type === "container");

  return (
    <div className="page-header">
      <div className="left-section flex items-center">
        <button type="button" className="navigation-toggle" onClick={onToggleNavigation} aria-label="Toggle navigation" aria-expanded={navigationOpen}><span aria-hidden="true">☰</span></button><img className="fleet-topbar-mark" src={fleetMark} alt="" /><div className="header-brand"><strong><span className="brand-smart">Smart</span> Transport</strong><span>Command Center</span></div>
      </div>

      <div className="center-section">
        <div className="search-bar-component" ref={searchBarRef}>
          <input
            type="search"
            value={searchQuery}
            onChange={handleSearchChange}
            aria-label="Search jobs and containers" placeholder="Find a job or container"
            onFocus={() => {
              setInputFocus(true);
              if (searchQuery.length > 0) {
                setShowDropdown(true);
              }
            }}
            onBlur={() => {
              setTimeout(() => {
                setInputFocus(false);
              }, 200);
            }}
            style={{
              borderBottomLeftRadius: showDropdown ? 0 : "5px",
            }}
          />
          <button aria-label="Search"
            className={inputFocus ? "focused-btn" : "not-focused-btn"}
            style={{
              borderBottomRightRadius: showDropdown ? 0 : "5px",
            }}
          >
            <SearchIcon />
          </button>

          {showDropdown && searchQuery.length > 0 && (
            <div className="search-suggestions">
              {isSearching ? (
                <div className="suggestion-section">
                  <div className="loading-indicator">Searching...</div>
                </div>
              ) : (
                <>
                  {jobs.length > 0 && (
                    <div className="suggestion-section">
                      <div className="section-header">Jobs</div>
                      {jobs.map((job) => (
                        <div
                          key={job.id}
                          className="suggestion-item"
                          onClick={() => handleItemSelect(job)}
                        >
                          <div className="item-title">
                            <div className="icon-cover">
                              <div className="icon">
                                <JobIcon />
                              </div>
                            </div>
                            {job.title}
                          </div>
                          {job.subtitle && (
                            <div className="item-subtitle">
                              <JobStatus jobStatus={job.subtitle.toString()} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {containers.length > 0 && (
                    <div className="suggestion-section">
                      <div className="section-header">Containers</div>
                      {containers.map((container) => (
                        <div
                          key={container.id}
                          className="suggestion-item"
                          onClick={() => handleItemSelect(container)}
                        >
                          <div className="container-cover">
                            <div className="item-title">
                              <div className="icon-cover">
                                <div className="icon">
                                  <ContainerIcon />
                                </div>
                              </div>
                              <div>
                                {container.title}
                                <div>
                                  {container.jobCode && (
                                    <div className="job-code">
                                      {container.jobCode}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="item-subtitle">
                            {container.size && container.containerType && (
                              <ContainerType
                                size={container.size}
                                type={container.containerType}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {jobs.length === 0 && containers.length === 0 && (
                    <div className="suggestion-section">
                      <div className="no-results">
                        <div className="no-results-message">
                          No results found
                        </div>
                        <div className="no-results-subtitle">
                          Try a different search term
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="right-section flex items-center">
        {showJobsSearch && (
          <PermissionGuard
            requiredPermissions={[PERMISSIONS.CAN_CREATE_JOB]}
            fallback={null}
          >
            <button
              type="button"
              onClick={() => navigate("/jobs/create-new")}
              className="primary_button m-r-10"
            >
              <div className="flex gap-2">
                <AddIcon />
                Create New
              </div>
            </button>
          </PermissionGuard>
        )}
        {isLiveFleet && <div id="fleet-header-controls" />}
        <ProfileButton />
      </div>
    </div>
  );
};

export default Header;
